import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { adminAPI, adminSession, type AdminEnrollmentChallenge } from '@/lib/api';
import { friendlyError } from '@/lib/errors';
import { federateToDev } from '@/lib/federation';
import { setActiveEnv, DEV_FEDERATION_FAILED_KEY } from '@/lib/environments';

// Land on /dashboard after a successful sign-in. The federated (dev
// session obtained) case is a normal client-side route change — convex.ts
// / api.ts stay wired to whatever was already active at page load (dev,
// the default with no session yet), which is the environment the admin
// is actually landing in, so nothing needs rebuilding.
//
// The non-federated case just flipped the active env to 'prod' via
// setActiveEnv(), but that only writes localStorage — convex.ts's
// ConvexReactClient and api.ts's API_BASE are both frozen from
// activeConfig() at module import, so without a real page load they'd
// stay pointed at dev (this page's load-time env) while adminSession /
// EnvBanner / EnvToggle all read localStorage live and report prod. Every
// dashboard query would then carry a prod token into dev-wired Convex
// calls, which reject it. This is exactly why EnvToggle reloads instead
// of navigating — force the same real load here.
//
// A full navigation drops React Router state, so the one-shot
// "dev federation failed" notice can't ride navigate()'s `state` the way
// it does on the federated path — stash it in sessionStorage instead;
// Dashboard reads and clears it on mount.
function landOnDashboard(navigate: ReturnType<typeof useNavigate>, federated: boolean) {
  if (federated) {
    navigate('/dashboard');
    return;
  }
  sessionStorage.setItem(DEV_FEDERATION_FAILED_KEY, '1');
  window.location.assign('/dashboard');
}

export function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the server says this admin hasn't enrolled an authenticator
  // yet → we switch to the QR enrollment step (email + password kept in
  // state for the enroll call).
  const [enroll, setEnroll] = useState<AdminEnrollmentChallenge | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await adminAPI.signin({ email, password, totp });
      if ('enrollment_required' in r) {
        // First sign-in — show the QR, ask for a confirming code.
        setEnroll(r);
        setTotp('');
        return;
      }
      // This session came from prod (signin is always pinned to
      // ENVIRONMENTS.prod.apiBase) — pin the write to 'prod' explicitly.
      // Relying on the default (the active env, which itself defaults to
      // 'dev') would file a prod token under dev's keys whenever no
      // wydm.admin.env is set yet, i.e. on every fresh login.
      adminSession.set(r.access, r.expires_at, r.admin, 'prod');
      // Get a dev session too, so toggling needs no second login. Awaited
      // (not fire-and-forget) so we know whether to land in dev — but a
      // dev federation problem must still never unwind the working prod
      // sign-in above.
      const federated = await federateToDev(r.access);
      if (!federated) {
        // No dev session exists. Landing in dev (the default) would show
        // a login screen despite a successful sign-in — land in prod,
        // where the admin is actually authenticated, and flag the dev
        // failure for the dashboard to surface.
        setActiveEnv('prod');
      }
      landOnDashboard(navigate, federated);
    } catch (err) {
      setError(friendlyError(err, 'SignIn'));
    } finally {
      setBusy(false);
    }
  };

  if (enroll) {
    return (
      <EnrollView
        email={email}
        password={password}
        challenge={enroll}
        onDone={async (r) => {
          // Same env pin as the direct sign-in path above, for the same
          // reason: this is a prod session regardless of what's active.
          adminSession.set(r.access, r.expires_at, r.admin, 'prod');
          // Enroll-totp also yields a real session (unlike the initial
          // enrollment_required challenge) — federate it too, and land
          // wherever the admin is actually authenticated.
          const federated = await federateToDev(r.access);
          if (!federated) setActiveEnv('prod');
          landOnDashboard(navigate, federated);
        }}
        onCancel={() => {
          setEnroll(null);
          setTotp('');
          setError(null);
        }}
      />
    );
  }

  return (
    <main style={{ maxWidth: 400, margin: '0 auto', padding: '80px 24px' }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--coral)', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
        WYDM Admin
      </div>
      <h1 className="serif" style={{ fontSize: 32, fontWeight: 500, lineHeight: 1.15, marginBottom: 8 }}>
        Sign in to <em>moderate</em>.
      </h1>
      <p style={{ fontSize: 12, color: 'var(--ink-dim, #888)', marginBottom: 20 }}>
        Authenticates against production. You can switch to development after signing in.
      </p>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <input type="email" name="email" autoComplete="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required style={input} />
        <input type="password" name="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required style={input} />
        <div>
          {/*
            autoComplete="one-time-code" is what makes 1Password (and iOS /
            Safari, and Chrome's SMS autofill) recognise this as the TOTP
            field. Without it a password manager fills email + password from
            the saved profile and silently skips this box, which reads as
            "the manager is broken" but is really us not labelling the field.

            `name` matters too: some managers fall back to name/id heuristics
            when the autocomplete token is absent or unrecognised.
          */}
          <input type="text" name="otp" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="6-digit code" value={totp} onChange={(e) => setTotp(e.target.value)} style={{ ...input, fontFamily: 'var(--font-mono)', letterSpacing: 4, textAlign: 'center' }} />
          <div style={fieldHint}>Skip on first sign-in — you’ll set up your authenticator next.</div>
        </div>
        {error && <div style={errorBox}>{error}</div>}
        <button type="submit" disabled={busy} style={primary}>{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </main>
  );
}

// First-sign-in enrollment: render the otpauth QR, let the new admin
// scan it (or paste the secret manually), then confirm a code → enrolled
// + signed in via /admin/auth/enroll-totp.
function EnrollView({
  email,
  password,
  challenge,
  onDone,
  onCancel,
}: {
  email: string;
  password: string;
  challenge: AdminEnrollmentChallenge;
  onDone: (r: { access: string; expires_at: string; admin: import('@/lib/api').Admin }) => void;
  onCancel: () => void;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(challenge.totp_uri, { margin: 1, width: 220 })
      .then((url) => alive && setQr(url))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [challenge.totp_uri]);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await adminAPI.enrollTotp({ email, password, totp });
      onDone(r);
    } catch (err) {
      setError(friendlyError(err, 'Enroll'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main style={{ maxWidth: 400, margin: '0 auto', padding: '64px 24px' }}>
      <div className="mono" style={{ fontSize: 11, color: 'var(--coral)', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>
        WYDM Admin · Set up
      </div>
      <h1 className="serif" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1.15, marginBottom: 8 }}>
        Set up your <em>authenticator</em>.
      </h1>
      <p style={{ fontSize: 14, color: 'var(--ink-dim, #666)', marginBottom: 20 }}>
        Scan this with Google Authenticator, 1Password, or any TOTP app, then enter the 6-digit code to finish.
      </p>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        {qr ? (
          <img src={qr} alt="Authenticator QR code" width={220} height={220} style={{ borderRadius: 12, background: '#fff', padding: 8 }} />
        ) : (
          <div style={{ width: 220, height: 220, display: 'grid', placeItems: 'center', background: 'var(--surface)', borderRadius: 12, fontSize: 13, color: '#999' }}>
            Generating QR…
          </div>
        )}
      </div>

      <details style={{ marginBottom: 18 }}>
        <summary style={{ fontSize: 13, color: 'var(--coral)', cursor: 'pointer' }}>Can’t scan? Enter this key manually</summary>
        <code style={{ display: 'block', marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 13, wordBreak: 'break-all', background: 'var(--surface)', padding: '10px 12px', borderRadius: 10 }}>
          {challenge.secret}
        </code>
      </details>

      <form onSubmit={confirm} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <input type="text" name="otp" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="6-digit code" value={totp} onChange={(e) => setTotp(e.target.value)} required autoFocus style={{ ...input, fontFamily: 'var(--font-mono)', letterSpacing: 4, textAlign: 'center' }} />
        {error && <div style={errorBox}>{error}</div>}
        <button type="submit" disabled={busy || totp.length < 6} style={primary}>{busy ? 'Confirming…' : 'Confirm & sign in'}</button>
        <button type="button" onClick={onCancel} style={ghost}>Back</button>
      </form>
    </main>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  background: 'var(--surface)',
  border: '1.5px solid transparent',
  borderRadius: 14,
  padding: '14px 16px',
  fontSize: 15,
  color: 'var(--ink)',
  outline: 'none' };
const primary: React.CSSProperties = {
  background: 'var(--coral)', color: '#fff', padding: '14px 24px',
  borderRadius: 100, fontWeight: 600, fontSize: 14, border: 'none' };
const ghost: React.CSSProperties = {
  background: 'transparent', color: 'var(--ink-dim, #666)', padding: '10px 24px',
  borderRadius: 100, fontWeight: 500, fontSize: 13, border: 'none', cursor: 'pointer' };
const errorBox: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)', color: '#a8331f', padding: '10px 14px', borderRadius: 12, fontSize: 13 };
const fieldHint: React.CSSProperties = {
  marginTop: 6, fontSize: 12, color: 'var(--ink-dim, #888)', textAlign: 'center' };
